/**
 * Atelier Hollis state — the Gold-only garment storefront inside the shop
 * (design/quick-specs/tailor-shop.md).
 *
 * ─── The one rule: fitting never equips ─────────────────────────────────────
 * The fitting stage's pieces live in THIS store (`fit`), and the preview reads
 * them through its own equipment source (`CharacterPreview.setFit`). Nothing
 * here writes `gameStateStore` except a completed PURCHASE, which writes the
 * inventory sheet the server sent back, exactly as the Black Market does.
 * Writing equipment from a Fit would really equip an unbought item, so
 * `atelier-fit.test.ts` holds it, and was mutation-checked against a Fit that
 * did.
 */
import { createStore } from '../../core/state/store';
import { api, ApiError } from '../../core/network/api';
import { gameStateStore } from './game-state';
import type {
  EquipmentSlot, EquippedItemSummary, InventorySheet,
} from './game-state.types';
import type {
  AtelierBundle, AtelierEntry, AtelierEvent, AtelierState, AtelierTab, FitPiece,
} from './atelier-state.types';

const INITIAL: AtelierState = {
  catalog: null,
  bundles: null,
  tab: 'HATS',
  fit: {},
  showBefore: false,
  busy: false,
  error: null,
  receipt: null,
  event: null,
};

export const atelierStore = createStore<AtelierState>({ ...INITIAL });

let seq = 0;
function event(kind: AtelierEvent['kind'], key: string): AtelierEvent {
  seq += 1;
  return { kind, key, seq };
}

/** The garment cards and the sets, in parallel; either failing leaves the other. */
export async function loadAtelier(): Promise<void> {
  atelierStore.set((s) => ({ ...s, busy: true, error: null }));
  const [catalog, bundles] = await Promise.all([
    api.get<AtelierEntry[]>('/shop/premium/catalog?storefront=TAILOR').catch((e: unknown) => e as Error),
    api.get<AtelierBundle[]>('/shop/premium/bundles').catch((e: unknown) => e as Error),
  ]);
  atelierStore.set((s) => ({
    ...s,
    busy: false,
    catalog: catalog instanceof Error ? s.catalog : catalog,
    bundles: bundles instanceof Error ? s.bundles : bundles,
    error: catalog instanceof Error ? msg(catalog, 'Hollis could not open the rail.') : null,
  }));
}

export function setAtelierTab(tab: AtelierTab): void {
  atelierStore.set((s) => ({ ...s, tab }));
}

// ─── Fitting (preview only) ──────────────────────────────────────────────────

/**
 * Puts a piece on the stage, replacing whatever is fitted in its slot. A piece
 * with no model yet is refused with Hollis's line instead of fitting nothing.
 */
export function fitPiece(piece: FitPiece, hasModel: boolean): void {
  if (!hasModel) {
    atelierStore.set((s) => ({ ...s, event: event('sewing', piece.itemKey) }));
    return;
  }
  atelierStore.set((s) => ({
    ...s, fit: { ...s.fit, [piece.slot]: piece }, showBefore: false, event: event('fit', piece.itemKey),
  }));
}

/** "Try full set": every piece at once, replacing the stage. */
export function fitSet(pieces: FitPiece[], key: string): void {
  const fit: AtelierState['fit'] = {};
  for (const p of pieces) fit[p.slot] = p;
  atelierStore.set((s) => ({ ...s, fit, showBefore: false, event: event('fit', key) }));
}

export function clearFitSlot(slot: EquipmentSlot): void {
  atelierStore.set((s) => {
    const fit = { ...s.fit };
    delete fit[slot];
    return { ...s, fit };
  });
}

export function clearFit(): void {
  atelierStore.set((s) => ({ ...s, fit: {}, showBefore: false }));
}

export function setShowBefore(showBefore: boolean): void {
  atelierStore.set((s) => ({ ...s, showBefore }));
}

/**
 * The fitted pieces as equipment slots, for `CharacterPreview.setFit`. Built
 * fresh from the store; never stored anywhere else. Empty under "Before".
 */
export function fitOverride(state: Pick<AtelierState, 'fit' | 'showBefore'>): Partial<Record<string, EquippedItemSummary>> {
  if (state.showBefore) return {};
  const out: Partial<Record<string, EquippedItemSummary>> = {};
  for (const [slot, p] of Object.entries(state.fit)) {
    if (!p) continue;
    out[slot] = {
      id: `fit:${p.itemKey}`,
      itemId: p.itemKey,
      def: {
        key: p.itemKey,
        name: p.name,
        slot: p.slot,
        kind: 'ARMOUR',
        rarity: p.stats.rarity,
        armorClass: p.stats.armorClass,
        armorPoints: p.stats.armorPoints,
        iconPath: p.iconPath,
        baseDamage: null,
        fixedDamage: null,
        ammoType: null,
        maxDurability: p.stats.maxDurability,
        statMods: { flat: { ...(p.stats.statMods.flat ?? {}) } },
      },
      durability: p.stats.maxDurability,
      maxDurability: p.stats.maxDurability,
      broken: false,
    };
  }
  return out;
}

/**
 * What the fitted pieces would change against what is worn now: armour points
 * and every flat stat, including set bonuses won or lost. Pure, so the stage
 * line and the tests read the same numbers.
 */
export function fitDelta(
  worn: Partial<Record<string, EquippedItemSummary>> | undefined,
  fit: AtelierState['fit'],
  bundles: AtelierBundle[] | null,
): { armor: number; flat: Record<string, number> } {
  const flat: Record<string, number> = {};
  const add = (mods: Record<string, number> | undefined, sign: number) => {
    for (const [k, v] of Object.entries(mods ?? {})) flat[k] = (flat[k] ?? 0) + sign * v;
  };
  let armor = 0;
  for (const [slot, piece] of Object.entries(fit)) {
    if (!piece) continue;
    const was = worn?.[slot];
    if (was && !was.broken) {
      armor -= was.def.armorPoints ?? 0;
      add(was.def.statMods?.flat, -1);
    }
    armor += piece.stats.armorPoints ?? 0;
    add(piece.stats.statMods.flat as Record<string, number> | undefined, 1);
  }
  const wornKeys = new Set(Object.values(worn ?? {}).filter((w) => w && !w.broken).map((w) => w!.itemId));
  const afterKeys = new Set(wornKeys);
  for (const [slot, piece] of Object.entries(fit)) {
    const was = worn?.[slot];
    if (was) afterKeys.delete(was.itemId);
    if (piece) afterKeys.add(piece.itemKey);
  }
  for (const b of bundles ?? []) {
    const before = b.pieces.every((p) => wornKeys.has(p.itemKey));
    const after = b.pieces.every((p) => afterKeys.has(p.itemKey));
    if (before !== after) add(b.bonus, after ? 1 : -1);
  }
  for (const k of Object.keys(flat)) if (Math.abs(flat[k]!) < 1e-9) delete flat[k];
  return { armor, flat };
}

// ─── Buying ──────────────────────────────────────────────────────────────────

interface BuyResponse { sheet: InventorySheet; balance: { cash: number; gold: number }; goldSpent: number }
interface SetBuyResponse extends BuyResponse { granted: string[]; alreadyOwned: string[] }

export async function buyAtelierItem(shopKey: string): Promise<void> {
  atelierStore.set((s) => ({ ...s, busy: true, error: null, receipt: null }));
  try {
    const r = await api.post<BuyResponse>(`/shop/premium/${shopKey}/buy`);
    gameStateStore.set((s) => ({ ...s, inventory: r.sheet }));
    atelierStore.set((s) => ({
      ...s,
      busy: false,
      catalog: s.catalog?.map((e) => (e.key === shopKey ? { ...e, owned: true } : e)) ?? null,
      receipt: `Wrapped and sent to your stash, ${r.goldSpent} Gold.`,
      event: event('sold', shopKey),
    }));
    void refreshBundles();
  } catch (err) {
    fail(err, shopKey);
  }
}

export async function buyAtelierSet(key: string): Promise<void> {
  atelierStore.set((s) => ({ ...s, busy: true, error: null, receipt: null }));
  try {
    const r = await api.post<SetBuyResponse>(`/shop/premium/bundles/${key}/buy`);
    gameStateStore.set((s) => ({ ...s, inventory: r.sheet }));
    const got = new Set(r.granted);
    atelierStore.set((s) => ({
      ...s,
      busy: false,
      catalog: s.catalog?.map((e) => (got.has(e.itemKey) ? { ...e, owned: true } : e)) ?? null,
      receipt: r.alreadyOwned.length
        ? `${r.granted.length} new pieces to your stash (you had ${r.alreadyOwned.length}), ${r.goldSpent} Gold.`
        : `The whole set, boxed and sent to your stash, ${r.goldSpent} Gold.`,
      event: event('set', key),
    }));
    void refreshBundles();
  } catch (err) {
    fail(err, key);
  }
}

async function refreshBundles(): Promise<void> {
  const bundles = await api.get<AtelierBundle[]>('/shop/premium/bundles').catch(() => null);
  if (bundles) atelierStore.set((s) => ({ ...s, bundles }));
}

function fail(err: unknown, key: string): void {
  const short = err instanceof ApiError && err.code === 'INSUFFICIENT_GOLD';
  atelierStore.set((s) => ({
    ...s,
    busy: false,
    error: short ? 'Not on credit, dear. Top up your Gold first.' : msg(err, 'Hollis could not finish the sale.'),
    event: short ? event('short', key) : s.event,
  }));
}

export function resetAtelierState(): void {
  atelierStore.set({ ...INITIAL });
}

function msg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
